import { useState, useEffect, useRef } from 'react';

interface UseCountUpOptions {
  duration?: number;
  startOnMount?: boolean;
  decimals?: number;
}

export function useCountUp(
  target: number,
  options: UseCountUpOptions = {}
) {
  const { duration = 500, startOnMount = true, decimals = 0 } = options;
  const [value, setValue] = useState(startOnMount ? 0 : target);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousTarget = useRef(target);
  const animationRef = useRef<number>();

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setValue(target);
      return;
    }

    const startValue = previousTarget.current !== target ? value : 0;
    const startTime = performance.now();
    setIsAnimating(true);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Cubic ease-out for satisfying deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (target - startValue) * easeOut;
      setValue(decimals > 0 ? parseFloat(currentValue.toFixed(decimals)) : Math.round(currentValue));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        previousTarget.current = target;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [target, duration, decimals]);

  const reset = () => {
    setValue(0);
    previousTarget.current = 0;
  };

  return { value, isAnimating, reset };
}

// Hook for triggering count-up when element is visible
export function useCountUpOnView(
  target: number,
  options: UseCountUpOptions & { threshold?: number; delay?: number } = {}
) {
  const { threshold = 0.2, delay = 0, ...countUpOptions } = options;
  const [isInView, setIsInView] = useState(false);
  const [shouldStart, setShouldStart] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  
  // Intersection Observer for visibility detection
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isInView) {
            setIsInView(true);
          }
        });
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, isInView]);

  // Handle staggered delay
  useEffect(() => {
    if (!isInView || shouldStart) return;
    
    if (delay > 0) {
      const timer = setTimeout(() => setShouldStart(true), delay);
      return () => clearTimeout(timer);
    } else {
      setShouldStart(true);
    }
  }, [isInView, delay, shouldStart]);

  const countUp = useCountUp(shouldStart ? target : 0, {
    ...countUpOptions,
    startOnMount: false,
  });

  // Track completion
  useEffect(() => {
    if (shouldStart && !countUp.isAnimating && countUp.value === target) {
      setHasCompleted(true);
    }
  }, [shouldStart, countUp.isAnimating, countUp.value, target]);

  return { 
    ...countUp, 
    elementRef, 
    hasStarted: shouldStart, 
    isInView,
    hasCompleted 
  };
}

// Hook for count-up with external visibility trigger (for shared containers)
export function useSharedCountUp(
  target: number,
  isVisible: boolean,
  options: UseCountUpOptions & { delay?: number } = {}
) {
  const { delay = 0, ...countUpOptions } = options;
  const [shouldStart, setShouldStart] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  // Handle staggered delay after visibility
  useEffect(() => {
    if (!isVisible || shouldStart) return;
    
    if (delay > 0) {
      const timer = setTimeout(() => setShouldStart(true), delay);
      return () => clearTimeout(timer);
    } else {
      setShouldStart(true);
    }
  }, [isVisible, delay, shouldStart]);

  const countUp = useCountUp(shouldStart ? target : 0, {
    ...countUpOptions,
    startOnMount: false,
  });

  // Track completion
  useEffect(() => {
    if (shouldStart && !countUp.isAnimating && countUp.value === target) {
      setHasCompleted(true);
    }
  }, [shouldStart, countUp.isAnimating, countUp.value, target]);

  return { 
    ...countUp, 
    hasStarted: shouldStart, 
    hasCompleted 
  };
}
